import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Domain, DomainsService } from './domains.service';

@Component({
    selector: 'app-domains',
    templateUrl: './domains.component.html',
    standalone: false,
})
export class DomainsComponent implements OnInit {
    domains: Domain[] = [];
    loading = true;
    showAddModal = false;
    expandedId: string | null = null;
    addForm: FormGroup;
    submitting = false;
    refreshingId: string | null = null;
    deletingId: string | null = null;
    showDeleteModal = false;
    domainToDeleteId: string | null = null;
    successMessage = '';
    errorMessage = '';
    siteIp: string | null = null;

    constructor(private service: DomainsService, private fb: FormBuilder) {
        this.addForm = this.fb.group({
            domain: ['', [Validators.required, Validators.pattern(/^(?!www\.)([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i)]],
            redirectWww: [true],
        });
    }

    ngOnInit() {
        this.load();
        this.service.siteIpAddress().subscribe({
            next: (ip) => (this.siteIp = ip),
            error: (err) => console.error('Failed to load site IP', err),
        });
    }

    load() {
        this.loading = true;
        this.service.list().subscribe({
            next: (domains) => {
                this.domains = domains;
                this.loading = false;
            },
            error: (err) => {
                console.error(err);
                this.errorMessage = 'Failed to load domains.';
                this.loading = false;
            },
        });
    }

    toggleExpand(id: string) {
        this.expandedId = this.expandedId === id ? null : id;
    }

    openAdd() {
        this.addForm.reset({ domain: '', redirectWww: true });
        this.showAddModal = true;
    }

    closeAdd() {
        this.showAddModal = false;
    }

    submitAdd() {
        if (this.addForm.invalid || this.submitting) return;
        const { domain, redirectWww } = this.addForm.value;
        this.submitting = true;
        this.service.create(domain.toLowerCase(), !!redirectWww).subscribe({
            next: (created) => {
                this.submitting = false;
                this.showAddModal = false;
                this.domains = [created, ...this.domains];
                this.expandedId = created._id;
                this.showSuccess(
                    created.status === 'issued'
                        ? 'Domain added — existing certificate reused.'
                        : 'Domain added. Add the DNS records below to validate the certificate.',
                );
            },
            error: (err) => {
                this.submitting = false;
                this.showError(extractMessage(err, 'Failed to add domain.'));
            },
        });
    }

    refresh(id: string) {
        this.refreshingId = id;
        this.service.refresh(id).subscribe({
            next: (updated) => {
                this.refreshingId = null;
                this.domains = this.domains.map((d) => (d._id === id ? updated : d));
            },
            error: (err) => {
                this.refreshingId = null;
                this.showError(extractMessage(err, 'Failed to refresh status.'));
            },
        });
    }

    delete(id: string) {
        this.domainToDeleteId = id;
        this.showDeleteModal = true;
    }

    confirmDelete() {
        const id = this.domainToDeleteId;
        if (!id) return;
        this.showDeleteModal = false;
        this.domainToDeleteId = null;
        this.deletingId = id;
        this.service.delete(id).subscribe({
            next: () => {
                this.deletingId = null;
                this.domains = this.domains.filter((d) => d._id !== id);
                this.showSuccess('Domain deleted.');
            },
            error: (err) => {
                this.deletingId = null;
                this.showError(extractMessage(err, 'Failed to delete domain.'));
            },
        });
    }

    cancelDelete() {
        this.showDeleteModal = false;
        this.domainToDeleteId = null;
    }

    copy(value: string | null | undefined) {
        if (!value) return;
        navigator.clipboard?.writeText(value).catch(() => {});
    }

    statusClass(status: string): string {
        switch ((status || '').toLowerCase()) {
            case 'issued':
                return 'bg-green-100 text-green-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-red-100 text-red-800';
        }
    }

    statusLabel(status: string): string {
        if (!status) return '';
        const lower = status.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }

    private showSuccess(message: string) {
        this.successMessage = message;
        this.errorMessage = '';
        setTimeout(() => (this.successMessage = ''), 4000);
    }

    private showError(message: string) {
        this.errorMessage = message;
        this.successMessage = '';
    }
}

function extractMessage(err: any, fallback: string): string {
    return err?.graphQLErrors?.[0]?.message || err?.message || fallback;
}
